import { useEffect, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Field, FormError, SubmitButton } from "@/components/common";
import { IntervalToggle, planPrice } from "@/pages/public/Pricing";
import { api, errMsg } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { t } from "@/i18n";

// Apostrophes are dropped (not hyphenated): "AD'S MECA" → "ads-meca".
export const slugify = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/['’‘`´]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50);

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function AccountStep({ onStarted }) {
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "" });
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/signup/start", f);
    onStarted(data);
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={run} className="space-y-5" data-testid="signup-account-form">
      <FormError message={error} />
      <Field label={t("p7.signup.fullName")} htmlFor="su-name"><Input id="su-name" required minLength={2} value={f.name} onChange={set("name")} className="h-11" data-testid="signup-name-input" /></Field>
      <Field label={t("auth.email")} htmlFor="su-email"><Input id="su-email" type="email" required autoComplete="email" value={f.email} onChange={set("email")} className="h-11" data-testid="signup-email-input" /></Field>
      <Field label={t("p7.signup.phone")} htmlFor="su-phone"><Input id="su-phone" type="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} className="h-11" data-testid="signup-phone-input" /></Field>
      <Field label={t("auth.password")} htmlFor="su-pw" hint={t("auth.passwordRule")}><Input id="su-pw" type="password" required minLength={8} autoComplete="new-password" value={f.password} onChange={set("password")} className="h-11" data-testid="signup-password-input" /></Field>
      <SubmitButton busy={busy} className="h-11 w-full" data-testid="signup-account-submit">{t("p7.signup.continue")}</SubmitButton>
    </form>
  );
}

export function CodeStep({ signup, onVerified, onRestart }) {
  const [code, setCode] = useState("");
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/signup/verify", { signup_id: signup.signup_id, code });
    onVerified(data);
  });
  const resend = async () => {
    try {
      await api.post("/signup/resend", { signup_id: signup.signup_id });
      toast.success(t("p7.signup.resent"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <form onSubmit={run} className="space-y-5" data-testid="signup-code-form">
      <p className="text-sm text-slate-600" data-testid="signup-code-sent">{t("p7.signup.codeSent", { email: signup.email })}</p>
      {signup.delivery !== "email" && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="signup-code-simulated">{t("p7.signup.codeSimulated")}</p>}
      <FormError message={error} />
      <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="signup-code-input">
        <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />)}</InputOTPGroup>
      </InputOTP>
      <SubmitButton busy={busy} disabled={code.length !== 6} className="h-11 w-full" data-testid="signup-code-submit">{t("p7.signup.verify")}</SubmitButton>
      <div className="flex justify-between text-sm">
        <button type="button" onClick={resend} className="font-medium text-teal-700 hover:text-teal-900" data-testid="signup-resend-code">{t("p7.signup.resend")}</button>
        <button type="button" onClick={onRestart} className="text-slate-500 hover:text-slate-800" data-testid="signup-change-email">{t("p7.signup.changeEmail")}</button>
      </div>
    </form>
  );
}

function SlugStatus({ slug }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (slug.length < 3) return setState(null);
    setState("checking");
    const id = setTimeout(() => api.get("/signup/slug-available", { params: { slug } }).then(({ data }) => setState(data.available ? "ok" : "taken")).catch((e) => setState(errMsg(e))), 350);
    return () => clearTimeout(id);
  }, [slug]);
  if (!state) return null;
  if (state === "checking") return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  if (state === "ok") return <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="signup-slug-ok"><Check className="h-3.5 w-3.5" />{t("p7.signup.slugOk")}</span>;
  return <span className="flex items-center gap-1 text-xs font-semibold text-red-600" data-testid="signup-slug-taken"><X className="h-3.5 w-3.5" />{state === "taken" ? t("p7.signup.slugTaken") : state}</span>;
}

export function PlanPicker({ plans, value, interval, onChange }) {
  return (
    <div className="grid gap-2" data-testid="signup-plan-picker">
      {plans.map((p) => {
        const price = planPrice(p, interval);
        return (
          <button type="button" key={p.id} disabled={!price} onClick={() => onChange(p.id)} data-testid={`signup-plan-${p.name.toLowerCase()}`}
            className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors duration-200 disabled:opacity-50 ${value === p.id ? "border-[#0A2540] bg-[#0A2540]/5 ring-1 ring-[#0A2540]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <span><span className="block font-semibold text-slate-900">{p.name}</span><span className="text-xs text-slate-500">{t("p7.pricing.trial", { n: p.trial_days })} · {t("p7.pricing.users", { n: p.max_internal_users })}</span></span>
            <span className="text-sm font-semibold text-slate-900">{price ? `${fmtMoney(price)}${t(interval === "yearly" ? "p7.plans.perYear" : "p7.plans.perMonth")}` : "—"}</span>
          </button>
        );
      })}
    </div>
  );
}

export function GarageStep({ plans, initialPlan, initialInterval, onCreated }) {
  const [f, setF] = useState({ garage_name: "", slug: "", plan_id: initialPlan || plans[0]?.id || "", interval: initialInterval || "monthly" });
  const [slugEdited, setSlugEdited] = useState(false);
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/signup/garage", f);
    await onCreated(data, f);
  });
  const setName = (e) => setF({ ...f, garage_name: e.target.value, slug: slugEdited ? f.slug : slugify(e.target.value) });
  return (
    <form onSubmit={run} className="space-y-5" data-testid="signup-garage-form">
      <FormError message={error} />
      <Field label={t("p7.signup.garageName")} htmlFor="su-gname"><Input id="su-gname" required minLength={2} value={f.garage_name} onChange={setName} className="h-11" data-testid="signup-garage-name-input" /></Field>
      <Field label={t("p7.signup.slug")} htmlFor="su-slug" hint={t("p7.signup.slugHint", { slug: f.slug || "your-garage" })}>
        <div className="flex items-center gap-3">
          <Input id="su-slug" required value={f.slug} onChange={(e) => { setSlugEdited(true); setF({ ...f, slug: slugify(e.target.value) }); }} className="h-11" data-testid="signup-slug-input" />
          <SlugStatus slug={f.slug} />
        </div>
      </Field>
      <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-800">{t("p7.signup.plan")}</p><IntervalToggle value={f.interval} onChange={(v) => setF({ ...f, interval: v })} testId="signup-interval" /></div>
      <PlanPicker plans={plans} value={f.plan_id} interval={f.interval} onChange={(v) => setF({ ...f, plan_id: v })} />
      <p className="text-xs text-slate-500">{t("p7.signup.terms")}</p>
      <SubmitButton busy={busy} disabled={!f.plan_id} className="h-11 w-full" data-testid="signup-garage-submit">{busy ? t("p7.signup.creating") : t("p7.signup.create")}</SubmitButton>
    </form>
  );
}

export function StepDots({ step, steps = ["account", "verify", "garage"] }) {
  const at = steps.indexOf(step);
  return (
    <ol className="mb-8 flex items-center gap-3 text-xs font-semibold" data-testid="signup-steps">
      {steps.map((s, i) => (
        <li key={s} className={`flex items-center gap-2 ${i <= at ? "text-[#0A2540]" : "text-slate-400"}`} data-testid={`signup-step-${s}`}>
          <span className={`flex h-6 w-6 items-center justify-center rounded-full ${i < at ? "bg-teal-600 text-white" : i === at ? "bg-[#0A2540] text-white" : "bg-slate-200"}`}>{i < at ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
          <span className="hidden sm:inline">{t(`p7.signup.steps.${s}`)}</span>
          {i < steps.length - 1 && <span className="h-px w-6 bg-slate-300" />}
        </li>
      ))}
    </ol>
  );
}

export const RestartButton = ({ onClick }) => <Button type="button" variant="ghost" onClick={onClick}>{t("p7.signup.changeEmail")}</Button>;
